
import React from 'react';

export const ProblemsSection: React.FC = () => {
  const problems = [
    {
      icon: 'ri-alarm-warning-line',
      title: 'Struggling to Keep Cash Flow Steady?',
      description: 'Insurance payments can take forever to come through, but your expenses do not wait. It\'s a constant juggling act to keep things running smoothly.',
      image: 'https://public.readdy.ai/ai/img_res/2eb5716a0fd981d9ecf28bb2d7926a6e.jpg'
    },
    {
      icon: 'ri-time-line',
      title: 'Feeling Worn Out From Doing Everything?',
      description: 'You\'re running the show, handling the books, managing billing, and putting out fires daily. It\'s no wonder you\'re exhausted—and still not seeing the payoff you deserve.',
      image: 'https://readdy.ai/api/search-image?query=Overwhelmed%20female%20doctor%20working%20late%20hours%20in%20medical%20office%2C%20surrounded%20by%20paperwork%20and%20administrative%20tasks%2C%20showing%20exhaustion%20from%20working%20in%20business%20instead%20of%20on%20business%2C%20professional%20medical%20setting&width=400&height=300&seq=problem2&orientation=landscape'
    },
    {
      icon: 'ri-eye-close-line',
      title: 'No Clear Handle on the Numbers?',
      description: 'Without solid systems to track and plan your finances, every month feels like a guessing game. You\'re reacting to problems instead of steering the business with confidence.',
      image: 'https://readdy.ai/api/search-image?query=Medical%20practice%20owner%20looking%20confused%20at%20financial%20reports%20and%20charts%2C%20unclear%20data%20visualization%2C%20financial%20confusion%2C%20professional%20office%20setting%2C%20representing%20lack%20of%20financial%20clarity%20and%20understanding&width=400&height=300&seq=problem3&orientation=landscape'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Not Sure What\'s Actually Making You Money?',
      description: 'It\'s hard to grow when you don\'t have a clear picture of which services are helping your bottom line—and which ones are hurting it.',
      image: 'https://readdy.ai/api/search-image?query=Financial%20charts%20showing%20declining%20profits%20and%20money%20flowing%20away%2C%20representing%20profit%20leaks%20in%20medical%20practice%2C%20professional%20business%20graphics%2C%20red%20arrows%20pointing%20down%2C%20financial%20loss%20visualization&width=400&height=300&seq=problem4&orientation=landscape'
    }
  ];

  return (
    <section id="problems" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The Hidden Perils of Poor Financial Management
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Without proper financial oversight, even successful practices can face devastating challenges that threaten their long-term viability.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {problems.map((problem, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48">
                <img 
                  src={problem.image}
                  alt={problem.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <i className={`${problem.icon} text-white text-xl`}></i>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{problem.title}</h3>
                <p className="text-gray-600 leading-relaxed">{problem.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-red-500 p-8 rounded-lg text-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-white text-3xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">The Cost of Inaction</h3>
              <p className="text-white text-lg max-w-3xl mx-auto">
                Insufficient financial management is one of the leading causes of profit erosion in private practices—impacting stability, scalability, and owner compensation. Don't let poor financial oversight be the reason your practice struggles to reach its full potential.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
